import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, KeyRound, Lock } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { validateNewPassword } from '../lib/authRecovery'

type ResetPasswordPageProps = {
  hasSession: boolean
}

export function ResetPasswordPage({ hasSession }: ResetPasswordPageProps) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setInfo(null)

    const validationError = validateNewPassword(password, confirmPassword)
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      setPassword('')
      setConfirmPassword('')
      setInfo('パスワードを更新しました。次回から新しいパスワードでログインできます。')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="min-h-full flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
            <KeyRound className="w-7 h-7" />
          </div>
          <h1 className="text-xl font-bold text-gray-800">パスワード再設定</h1>
          <p className="text-xs text-gray-500 mt-1 text-center">
            メールのリンクから開いた後、新しいパスワードを設定します。
          </p>
        </div>

        {!hasSession && (
          <div className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-4">
            再設定リンクの有効期限が切れている可能性があります。もう一度メールを送信してください。
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">新しいパスワード</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute top-2.5 left-3 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                placeholder="8文字以上"
                disabled={!hasSession || loading}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">確認用パスワード</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute top-2.5 left-3 text-gray-400" />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                placeholder="もう一度入力"
                disabled={!hasSession || loading}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          {info && (
            <div className="flex gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{info}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={!hasSession || loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
          >
            <KeyRound className="w-4 h-4" />
            {loading ? '更新中…' : 'パスワードを更新'}
          </button>
        </form>

        <Link
          to="/login"
          className="mt-4 block text-center text-sm text-gray-500 hover:text-gray-800"
        >
          ログイン画面へ戻る
        </Link>
      </div>
    </main>
  )
}
